import { CheckIcon } from 'lucide-react';
import { type FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { cn } from '@heidi/utils';

import { Button } from '../button';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { Separator } from '../separator';
import { menuItemVariants } from '../styles';

import { DateRangePickerContent } from './DateRangePickerContent';
import { type Preset, type PresetName, PRESETS } from './presets';
import type { DateRange, DateRangePickerProps } from './types';
import { getPresetRange } from './utils';

interface DateRangePickerWithPresetsProps {
  range: DateRange;
  setRange: (range: DateRange) => void;
  /**
   * Which row reads as selected. Supplying it takes the choice off the body,
   * which otherwise infers it by matching `range` against the offered presets —
   * the inference cannot separate two that resolve to the same dates.
   *
   * `null` is not "controlled, nothing ticked": it means the same as leaving the
   * prop out, which is to hand the choice back and resume inferring.
   *
   * Pair it with `onPresetChange`, which is the only thing that moves the
   * selection while it is non-null. Every source of a change reports through
   * that callback — a preset row with its own name, a pick in the calendar grid
   * with `custom` — so a host echoing the name back cannot end up ticking a
   * preset over a range the user has since edited by hand.
   */
  activePresetName?: PresetName | null;
  onPresetChange?: (presetName: PresetName) => void;
  /**
   * Which presets the column offers. It selects a subset, it does not reorder:
   * rows always render in `PRESETS` order (chronological, `custom` last), so
   * `['last90', 'last7']` renders as `last7, last90`. The fixed order is what
   * lets the row loop hang the separator off `custom` being the tail.
   */
  presetNames?: PresetName[];
  /** @deprecated Unread — the body no longer owns popover state. Kept so the ~15 existing hosts keep compiling. */
  isOpen?: boolean;
  /** @deprecated Unread — see `isOpen`. */
  setIsOpen?: (isOpen: boolean) => void;
  /**
   * Which days the calendar grid refuses. Preset rows are NOT gated, so a
   * preset can still set a range spanning refused days (`last90` against a rule
   * that blocks everything before last month, say). A host that needs the
   * column to agree has to narrow `presetNames` itself.
   */
  disabledDates?: DateRangePickerProps['disabledDates'];
}

const DEFAULT_PRESET_NAMES: PresetName[] = [
  'last7',
  'last30',
  'last90',
  'last12months',
  'custom',
];

export const DateRangePickerWithPresets: FC<DateRangePickerWithPresetsProps> = ({
  range,
  setRange,
  activePresetName,
  onPresetChange,
  presetNames = DEFAULT_PRESET_NAMES,
  disabledDates,
}) => {
  const intl = useDesignSystemIntl();
  const isControlled = activePresetName != null;
  const [inferredPreset, setInferredPreset] = useState<PresetName | null>(null);
  const [inferredIsCustom, setInferredIsCustom] = useState(false);

  const presetsWithCustom: Preset[] = useMemo(
    () => PRESETS.filter((p) => presetNames.includes(p.name)),
    [presetNames]
  );

  useEffect(() => {
    if (isControlled) return;

    // Only the rows on offer, and never `custom`: searching all of `PRESETS`
    // let a range match a preset this column hides — a single day matches
    // `today` — which ticked no row at all, not even `custom`.
    const match = presetsWithCustom.find((p) => {
      if (p.name === 'custom') return false;
      const { from: pf, to: pt } = getPresetRange(p.name);
      const normalize = (d?: Date) => (d ? new Date(d).setHours(0, 0, 0, 0) : null);
      return (
        normalize(range.from) === normalize(pf) && normalize(range.to) === normalize(pt)
      );
    });
    setInferredPreset(match ? match.name : null);
    setInferredIsCustom(!match);
  }, [isControlled, presetsWithCustom, range]);

  /**
   * Rendered from `activePresetName` whenever the host supplies one, rather
   * than from state a click also writes. Those two could disagree: a host that
   * controlled the name but ignored `onPresetChange` showed the clicked row as
   * selected while its own value said otherwise, until the range next moved.
   */
  const activePreset = isControlled ? activePresetName : inferredPreset;
  const isCustom = isControlled ? activePresetName === 'custom' : inferredIsCustom;

  const setPreset = useCallback(
    (preset: Preset) => {
      setRange(
        preset.name === 'custom'
          ? { from: undefined, to: undefined }
          : getPresetRange(preset.name)
      );
      onPresetChange?.(preset.name);

      if (isControlled) return;
      setInferredPreset(preset.name);
      setInferredIsCustom(preset.name === 'custom');
    },
    [isControlled, onPresetChange, setRange]
  );

  /**
   * Only the calendar grid arrives here — `setPreset` writes the range itself,
   * and the date inputs are hidden under `hideCustomDates`. Reporting `custom`
   * from this one place is what saves every host from knowing that a preset row
   * calls `setRange` first and names itself second.
   */
  const setRangeFromCalendar = useCallback(
    (next: DateRange) => {
      setRange(next);
      onPresetChange?.('custom');
    },
    [onPresetChange, setRange]
  );

  return (
    <div className="flex h-full gap-3 p-3">
      <div className="flex w-48 flex-col">
        {presetsWithCustom.map((p) => {
          const isActive =
            (activePreset === p.name && !isCustom) || (isCustom && p.name === 'custom');
          return (
            <Fragment key={p.name}>
              {p.name === 'custom' && <Separator className="my-1" />}
              {/*
               * `Button` stays the element (DS-1620) — it brings `focusRing`,
               * which the shared menu recipe leaves to the Radix menu it
               * assumes it is inside. `size="sm"` is already the recipe's 32px
               * / `rounded-md` row, so the className only has to supply the
               * colour ladder and the selected state.
               *
               * The two `disable*` props and `font-normal` switch off treatments
               * the recipe owns instead: `size="sm"` would otherwise apply
               * `p2Bold` + `leading-8` and a 14px icon, and the button base sets
               * `font-medium` — a menu row is 400.
               */}
              <Button
                variant="ghost"
                size="sm"
                disableAutomaticTypographyClasses
                disableAutomaticSvgSizing
                className={cn(
                  menuItemVariants({ variant: isActive ? 'selected' : 'default' }),
                  'justify-between font-normal'
                )}
                onClick={() => setPreset(p)}
              >
                {intl.formatMessage(p.label)}
                {/*
                 * Explicit `size-4` rather than leaning on the recipe: under
                 * the experimental forms flag `disableAutomaticSvgSizing` is
                 * not a total bypass — it still sizes a `width="24"` icon that
                 * opted out neither by class nor by prop, which is what a bare
                 * lucide icon is. A `size-*` class is the per-icon opt-out both
                 * tiers honour.
                 */}
                {isActive && <CheckIcon className="size-4" />}
              </Button>
            </Fragment>
          );
        })}
      </div>
      <div className="border-s" />
      <DateRangePickerContent
        range={range}
        setRange={setRangeFromCalendar}
        monthsLayout="row"
        hideCustomDates
        disabledDates={disabledDates}
      />
    </div>
  );
};

DateRangePickerWithPresets.displayName = 'DateRangePickerWithPresets';
